import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'

export interface UseImageLoadingStateProps {
  src: string | null | undefined
}

export interface UseImageLoadingStateResult {
  /**
   * Callback to pass to the `img` tag.
   */
  onLoad?: () => unknown

  /**
   * Whether the image has been loaded.
   */
  loaded: boolean
}

/**
 * Allows tracking the loading state of an image.
 * Must be used inside a {@link LoadedImageUrlsCacheContextProvider}.
 *
 * @param src
 */
export function useImageLoadingState({
  src,
}: UseImageLoadingStateProps): UseImageLoadingStateResult {
  const cache = useContext(LoadedImageUrlsCacheContext)
  const [loaded, setLoaded] = useState(Boolean(src && cache?.has(src)))
  const onLoad = useCallback(() => {
    if (src) {
      cache?.add(src)
    }

    setLoaded(true)
  }, [src, cache])

  if (!loaded && src && cache?.has(src)) {
    setLoaded(true)
  }

  return { onLoad: loaded ? undefined : onLoad, loaded: loaded }
}

/**
 * Contains a set of loaded URLs to prevent flashing the loader.
 */
export const LoadedImageUrlsCacheContext = createContext<Set<string> | null>(
  null,
)

/**
 * Provides a shared cache to all avatar components for loaded URLs.
 * @constructor
 */
export function LoadedImageUrlsCacheContextProvider({
  children,
}: {
  children?: ReactNode
}) {
  const cacheRef = useRef<Set<string>>(null)
  cacheRef.current ??= new Set()

  return (
    <LoadedImageUrlsCacheContext value={cacheRef.current}>
      {children}
    </LoadedImageUrlsCacheContext>
  )
}
